import { twMerge } from '@shared/themes';
import { useTranslations } from 'next-intl';
import type { ComponentProps, ReactNode } from 'react';
import { NavigationLink } from '../NavigationLink/NavigationLink';

type NavigationLinksProps = ComponentProps<'ul'> & {
  namespace: string;
  links?: NavigationLinkItem[];
};

type NavigationLinkItem = {
  href: string;
  text: string;
  trackName: string;
  icon?: ReactNode;
  activePaths?: string[];
};

/**
 * Navigation links component - renders a list of navigation links
 * @param namespace - The namespace of the translation
 * @param className - (optional) The class name of the component
 * @param props - (optional) The props of the component
 * @returns A navigation HTMLUnorderedList component with a NavigationLink components inside
 */
function NavigationLinks({ namespace, className, links = [], ...props }: NavigationLinksProps) {
  const t = useTranslations(namespace);
  return (
    <ul
      className={twMerge('flex flex-row gap-1 overflow-x-auto whitespace-nowrap hide-scrollbar pl-3 -ml-3 touch-pan-x', className)}
      data-testid="navigation-bar"
      data-track-container="Navigation"
      {...props}
    >
      {links.map(({ text, href, trackName, icon, activePaths }) => (
        <NavigationLink key={text} href={href} trackName={trackName} activePaths={activePaths}>
          {icon} <span>{t(text)}</span>
        </NavigationLink>
      ))}
    </ul>
  );
}

export { NavigationLinks };
export type { NavigationLinkItem };
